/**
 * MasterCell (Phase 3.4) — first-column cell of `<PostsGrid>`.
 *
 * One per aspect row. Renders the master post + exposes Regen + Delete
 * actions. Phase 3 brings:
 *   - Regen flow: confirm dialog → archive old + mark derivatives stale
 *     → kick planned `/generate-posts` with cellScope + planner envelope.
 *   - Stale propagation: derivatives in row pick up the staleSince marker
 *     immediately after `prepareMasterRegen`; the row-level batch action
 *     handles bulk regeneration of those derivatives (Phase 3.5).
 *   - Delete: removes the master doc; derivatives are NOT cascaded —
 *     they remain in `content_posts/*` with a stale `sourcePostId`. UI
 *     surfaces them as orphan in the cell with a delete affordance.
 */

import React, { useState } from 'react';
import {
  Box, Typography, Stack, IconButton, Tooltip, Chip, CircularProgress,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { SDialog } from '@modules/ui-components';
import { AvailableModelSelector } from '@modules/ai-settings';
import { ContentPlanApiClient } from '../../services/ContentPlanApiClient';
import { contentPostGridService } from '../../services/ContentPostGridService';
import { useProjectDefaultModel } from '../../hooks/useProjectDefaultModel';
import type { MasterCellModel } from '../../hooks/useTopicPostsGrid';
import { useTask, ACTIVE_STATUSES } from '@modules/background-tasks';
import { CellTaskBadge } from './CellTaskBadge';

export interface MasterCellProps {
  projectId: string;
  userId: string;
  authorId: string;
  topicId: string;
  /** Aspect this master targets — null for single-aspect topics. */
  aspectKey: string | null;
  model: MasterCellModel | undefined;
  busy: boolean;
  /**
   * Phase 3 follow-up — backend task id for an in-flight generation that
   * has not yet produced a post doc. Owned by `PostsGrid`. When set AND
   * the cell is still empty, the empty state renders a live
   * `<CellTaskBadge>` instead of the wand.
   */
  pendingTaskId?: string | null;
  /** Strategy + sprint context — needed when generation should auto-apply to a sprint. */
  strategyId?: string;
  sprintId?: string;
  regenPlatform?: import('../../types').PlatformId | null;
  regenPlatforms?: import('../../types').PlatformId[];
  onGenerate: () => void;
  /** Phase 3.6 — click on the post body opens the post in UnifiedPostDialog
   *  (mounted at PostsGrid level to share one dialog instance across cells). */
  onOpenPost?: (post: import('../../types').Post) => void;
  /** Orphan-aspect rows surface posts whose aspectKey is not in `topic.postBreakdown`
   *  (legacy posts created before aspects were added, or posts whose aspect was
   *  removed). Generating into an orphan would write more null-aspect posts, so
   *  the wand is hidden — user can edit/delete existing posts via the body click. */
  disableGenerate?: boolean;
  disableGenerateReason?: string;
}

export const MasterCell: React.FC<MasterCellProps> = ({
  projectId, userId, authorId, topicId, aspectKey, model, busy, pendingTaskId,
  strategyId, sprintId, regenPlatform, regenPlatforms, onGenerate, onOpenPost, disableGenerate, disableGenerateReason,
}) => {
  const testidBase = `posts-grid-master-${aspectKey ?? 'noaspect'}`;
  const state = model?.state ?? 'empty';
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const [regenBusy, setRegenBusy] = useState(false);
  const [regenDialogOpen, setRegenDialogOpen] = useState(false);
  const [regenModel, setRegenModel] = useState(projectDefaultModel.value);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (regenDialogOpen) setRegenModel(projectDefaultModel.value);
  }, [regenDialogOpen, projectDefaultModel.value]);

  // Live status: subscribe to the post's lastTaskId. When the task is in any
  // ACTIVE_STATUSES, the cell is "generating" — disable mutating buttons and
  // render the badge. Source of truth replaces local `regenBusy`.
  const liveTaskId = (model?.master as { lastTaskId?: string } | undefined)?.lastTaskId ?? null;
  const { task: liveTask } = useTask(liveTaskId);
  const liveActive = liveTask
    ? ACTIVE_STATUSES.includes(liveTask.status as typeof ACTIVE_STATUSES[number])
    : false;

  const handleRegen = async (modelId: string) => {
    if (!model?.master) return;
    const oldMaster = model.master;
    setRegenBusy(true);
    setError(null);
    try {
      // 1) Archive old master + mark derivatives stale (atomic flow).
      await contentPostGridService.prepareMasterRegen(projectId, oldMaster.id);
      const targetPlatforms = (regenPlatforms?.length ? regenPlatforms : (regenPlatform ? [regenPlatform] : []))
        .map((item) => String(item).trim())
        .filter(Boolean);
      if (targetPlatforms.length === 0) {
        throw new Error('Add an author/topic platform before regenerating the master post.');
      }
      // 2) Generate replacement through the planned post-generation path so
      //    PostGenerationPlanner runs before MasterPostHandler/DerivativePostHandler.
      const client = new ContentPlanApiClient();
      const res = await client.generatePosts({
        projectId,
        userId,
        authorId,
        scope: sprintId ? 'sprint' : 'topic',
        strategyId,
        sprintId,
        topicIds: [topicId],
        platforms: targetPlatforms,
        cellScope: {
          aspectKey,
          platform: targetPlatforms[0],
          platforms: targetPlatforms,
          cellMode: 'single',
          method: 'cope',
        },
        applyMode: sprintId ? 'auto_apply_sprint' : 'auto_apply_topic',
        models: { platformPost: modelId },
      } as any);
      if (!res.success) throw new Error(res.error ?? 'Master regen failed');
      // Cell will pick up the new master automatically via useTopicPostsGrid
      // snapshot once the backend setImmediate finishes (~5-15s for Gemini).
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRegenBusy(false);
      setRegenDialogOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!model?.master) return;
    if (!window.confirm('Delete master post? Derivatives in this row will not be cascaded.')) return;
    setError(null);
    try {
      await contentPostGridService.deletePost(projectId, model.master.id);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // ── Empty state ─────────────────────────────────────────────────
  if (state === 'empty') {
    return (
      <Box
        sx={{
          p: 1, borderRadius: 1, border: '1px dashed',
          borderColor: 'divider',
          minHeight: 64, display: 'flex', alignItems: 'center',
          justifyContent: 'center',
        }}
        data-testid={testidBase}
      >
        {disableGenerate ? (
          <Tooltip title={disableGenerateReason ?? 'Orphan aspect — generation disabled. Edit or delete existing posts in this row, or create a new aspect in postBreakdown.'}>
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>—</span>
          </Tooltip>
        ) : pendingTaskId ? (
          // Live status while the dialog-triggered generation is in flight
          // and has not yet written a post-doc to Firestore. The badge
          // auto-hides when the task hits a terminal status.
          <CellTaskBadge
            taskId={pendingTaskId}
            data-testid={`${testidBase}-task-badge-pending`}
          />
        ) : (
          <Tooltip title={busy ? 'Generating…' : 'Generate master post'}>
            <span>
              <IconButton
                size="small"
                onClick={onGenerate}
                disabled={busy}
                data-testid={`${testidBase}-generate`}
                color="primary"
              >
                {busy ? <CircularProgress size={16} /> : <AutoFixHighIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    );
  }

  // ── Applied / stale state ───────────────────────────────────────
  const post = model!.master!;
  return (
    <Box
      sx={{
        p: 1, borderRadius: 1, border: '1px solid',
        borderColor: state === 'stale' ? 'warning.main' : 'divider',
        minHeight: 64,
      }}
      data-testid={testidBase}
    >
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
        <Chip label="master" size="small" color="primary" variant="outlined" />
        {state === 'stale' && (
          <Chip label="stale" size="small" color="warning" />
        )}
        <CellTaskBadge
          taskId={liveTaskId}
          dense
          data-testid={`${testidBase}-task-badge`}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Regenerate master (marks derivatives stale)">
          <span>
            <IconButton
              size="small"
              onClick={() => {
                setRegenModel(projectDefaultModel.value);
                setRegenDialogOpen(true);
              }}
              disabled={regenBusy || liveActive}
              data-testid={`${testidBase}-regen`}
              sx={{ p: 0.25 }}
            >
              {regenBusy || liveActive ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete master">
          <span>
            <IconButton
              size="small"
              onClick={handleDelete}
              data-testid={`${testidBase}-delete`}
              sx={{ p: 0.25 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.5 }}>
          {error}
        </Typography>
      )}
      <Typography
        variant="body2"
        onClick={() => onOpenPost?.(post)}
        sx={{
          fontWeight: 500,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          cursor: onOpenPost ? 'pointer' : 'default',
          '&:hover': onOpenPost ? { textDecoration: 'underline', color: 'primary.main' } : undefined,
        }}
        data-testid={`${testidBase}-open`}
      >
        {post.hook ?? post.title ?? '(master post)'}
      </Typography>
      <SDialog
        open={regenDialogOpen}
        onClose={() => setRegenDialogOpen(false)}
        title="Regenerate master"
        maxWidth="sm"
        fullWidth
        primaryAction={{
          label: regenBusy ? 'Regenerating…' : 'Regenerate',
          onClick: () => handleRegen(regenModel),
          loading: regenBusy,
          disabled: regenBusy,
          'data-testid': `${testidBase}-regen-confirm`,
        } as any}
        secondaryAction={{
          label: 'Cancel',
          onClick: () => setRegenDialogOpen(false),
          disabled: regenBusy,
        } as any}
      >
        <Stack spacing={2} sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Existing derivatives in this row will be marked stale. You can
            regenerate them with the row stale-regeneration action after the
            new master is ready.
          </Typography>
          <AvailableModelSelector
            projectId={projectId}
            value={regenModel}
            onChange={setRegenModel}
            label="AI model"
            size="small"
          />
        </Stack>
      </SDialog>
    </Box>
  );
};

export default MasterCell;
